@page "/start"
@import Code from "../components/Code.xzor"
@import { href } from "../routes"

<section class="page narrow">
  <h1>Get started</h1>
  <p class="lead">One command sets up an empty folder or an existing project.</p>

  <div class="two">
    <Code name="install-win" />
    <Code name="install-unix" />
  </div>

  <p>The installer:</p>
  <ol class="checklist">
    <li>installs <a href="https://bun.sh" rel="external">Bun</a> if you don't have it;</li>
    <li>adds the compiler, the runtime and TypeScript 7;</li>
    <li>runs <code>xzor init</code>: it creates <code>package.json</code>, <code>tsconfig.json</code> and <code>.gitignore</code>, or adds only what's missing to the ones you already have, plus a starter app if there is no <code>main.ts</code> (<code>xzor init --server</code> writes a server app instead: <code>client/</code> and <code>server/</code> with one endpoint, <code>contracts/</code> for shared types, and adds Bun's types);</li>
    <li>installs the VS Code extension if <code>code</code> is on your PATH.</li>
  </ol>
  <p>
    On <strong>Zed</strong>, install the extension in <code>editors/zed</code> of the xzor repo with <code>zed: install dev extension</code>, keep
    Zed's HTML extension installed, and add <code>{"languages": {"XZor": {"semantic_tokens": "combined"}}}</code> to the project's
    <code>.zed/settings.json</code>: the language server colors the xzor and TypeScript parts.
  </p>

  <h2>Run it</h2>
  <Code name="run" />
  <p>
    <code>xzor serve</code> builds with <code>--minify</code> and serves the app the way it ships (<code>--port</code>, <code>--host</code>;
    it listens on <code>127.0.0.1</code> unless you pass <code>--host</code>).
  </p>
  <p>
    Save a <code>.xzor</code> file and the open page shows the change at once. Only the components you changed are
    updated in place, and they keep their state (field values, focus, scroll). Any other change reloads the page. A
    <code>#private</code> member, a new import or a changed base class also reload, and arrow-function fields keep their old
    code until a reload.
  </p>

  <h2>Installable and offline</h2>
  <p>
    Put a <code>manifest.webmanifest</code> next to <code>index.html</code>, with a 192x192 and a 512x512 icon, and link it
    with <code>&lt;link rel="manifest" href="manifest.webmanifest"&gt;</code>.
  </p>
  <Code name="manifest" />
  <p>
    <code>xzor build</code> then writes <code>sw.js</code> beside <code>index.html</code> and registers it. <code>xzor init</code>
    puts <code>sw.js</code> in <code>.gitignore</code>; deploy it with the app. Offline, a page load falls back to the cached app,
    and after you deploy, a new version shows up on the second load. Nothing is cached in <code>xzor dev</code>.
  </p>

  <h2>Prefer to do it by hand?</h2>
  <Code name="manual" />
  <p>The settings <code>xzor init</code> makes sure of:</p>
  <Code name="tsconfig" />

  <h2>The editor</h2>
  <p>
    The extension gives you highlighting, completions, hover, go to definition and live errors, including inside the
    <code>@@</code> expressions and the <code>@@code</code> block. Install <code>xzor-&lt;version&gt;.vsix</code> from the
    <a href="https://github.com/pedro-gilmora/source-crafter-xzor/releases" rel="external">releases page</a>.
  </p>

  <a class="xz-button" data-variant="solid" data-tone="accent" href="@href.Learn({ step: 1 })">Next: learn it in eight steps</a>
</section>
